/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2015, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'./BaseView',
	'jquery',
	'underscore',
	'../services/ServicesHelper',
	'../../app/EventRouter',
	'../loader/BoardLoader',
	'../layout/authoring/interaction/ChangeModeAction',
	'../util/ButtonHideHelper',
	'../../app/util/ErrorUtils',
	'../../app/ui/dialogs/EditableDialog',
	'../../lib/@waca/core-client/js/core-client/utils/PerfUtils',
	'../../lib/@waca/core-client/js/core-client/ui/KeyCodes',
	'../services/CanvasExtensions',
	'../../api/impl/Dashboard',
	'../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../lib/@waca/dashboard-common/dist/utils/EventChainLocal',
	'../../app/nls/StringResources',
	'../../lib/@waca/dashboard-common/dist/utils/DialogBlocker',
	'../../extension/Extensions',
	'../widgets/WidgetRegistry',
	'../../dashboard/model/BoardModelFactory',
	'../../lib/@waca/dashboard-common/dist/utils/MemUtil',
	'../../api/core/glass/GlassDashboardBuilder'

], function(
	BaseView,
	$,
	_,
	ServicesHelper,
	EventRouter,
	BoardLoader,
	ChangeModeAction,
	ButtonHideHelper,
	ErrorUtils,
	EditableDialog,
	PerfUtils,
	KeyCodes,
	CanvasExtensions,
	DashboardController,
	APIFactory,
	EventChainLocal,
	StringResources,
	DialogBlocker,
	Extensions,
	WidgetRegistry,
	BoardModelFactory,
	MemUtil,
	GlassDashboardBuilder
) {

	if (typeof window.__getDashboardAPI === 'undefined') {
		window.__getDashboardAPI = function() {
			return window.__glassAppController.Glass.getCurrentContentView().getDashboardApi();
		};
	}

	const BaseBoardView = BaseView.extend({

		buttonIds: {
			UNDO: 'com.ibm.bi.dashboard.undo',
			REDO: 'com.ibm.bi.dashboard.redo'
		},

		init: function(options, perspectiveController, perspectiveModel) {
			BaseBoardView.inherited('init', this, arguments);
			this.dashboardApi = null;
			this._resolveOptions(options);
			_.extend(this, options);
			this.options = options || {};
			this.firstRender = true;
			this._perspectiveModel = perspectiveModel;
		},

		initialize: function(options) {
			if(!this.initPromise) {

				this.dashboardBuilder = GlassDashboardBuilder.create({
					glassContext: this.glassContext,
					perspectiveModel: this._perspectiveModel,
					options: options,
					apiConsumerConfig:{
						contentFeatureDelayLoadTabs: !!this.contentFeatureDelayLoadTabs
					}
				});

				this.initPromise = this.dashboardBuilder.getBootstrapFeatures().then( features => {
					const dirtyOption = String(options._isDirty).toLowerCase() === 'true';
					this.options['isDirtyOption'] = dirtyOption;
					this._pressed = {};
					this._boardLoaderClass = options.boardLoaderClass || BoardLoader;
					this.eventRouter = new EventRouter();
					this._buttonHideHelper = options._buttonHideHelper || ButtonHideHelper;
					this._splitterItems = [];
					this._modifiedDataset = [];
					this.upgradesCollectionPath = 'dashboard/upgrades';
					this.logger = features['Logger'].getAPI();
					this.$content = null;
					this.widgetRegistry = options.widgetRegistry || new WidgetRegistry();
					this._createController(options, features);
					this._prepareForRendering(options, features);
				});
			}

			return this.initPromise;
		},

		/**
		 * Creates services and extensions required for rendering the dashboard
		 * @param {object} options consist of attributes required to create services & extensions for rendering dashboard
		 */
		_prepareForRendering: function(options, features) {
			this._createServices(features);

			// merge the containerApp (API container) configuration with the content options config
			if (options.containerAppOptions) {
				_.extend(this.options.options.config, options.containerAppOptions.configuration);
			}
			// Allow preprocessKeyDown to be both overwritten by an extending class directly or via a call to super
			if (options.preprocessKeyDown) this.preprocessKeyDown = options.preprocessKeyDown;
			this.specHelper = null;

			this.extensions = options.extensions || new Extensions(this.dashboardApi);

			this.boardModelFactory = options.boardModelFactory;
		},

		_createController: function(options, features) {
			const profiles = options.boardId ?  ['core'] : ['core','consume','authoring'];

			let DashboardControllerClass = options.overrideDashboardController ? options.overrideDashboardController: DashboardController;
			this.dashboardController = options.dashboardController || new DashboardControllerClass({
				glass: features['Glass'].getAPI(),
				settings: features['DashboardSettings'].getAPI(),
				view: this,
				services: this.services,
				featureLoader: this.featureLoader,
				profiles: profiles,
				eventRouter: this.eventRouter,
				appSettings: this.getContent({
					dirty: this.options.isDirtyOption
				}),
				widgetRegistry: this.widgetRegistry
			});
			if (this.dashboardApi) {
				this.dashboardApi = APIFactory.refreshAPI(this.dashboardApi, [this.dashboardController]);
				this.internalDashboardApi = APIFactory.refreshAPI(this.internalDashboardApi, [this.dashboardController, this.dashboardController.internalImpl]);

			} else {
				this.dashboardApi = this.dashboardController.getAPI();
				this.internalDashboardApi =  this.dashboardController.getInternalAPI();
			}
		},

		getApplicationLabel: function() {
			return StringResources.get('dashboard');
		},

		getContentNode: function() {
			if (this.$content === null) {
				let $contentNode = this.$('.pageViewContent', this.el);
				if ($contentNode.length === 0) {
					$contentNode = $('<div class="pageViewContent"></div>').prependTo(this.el);
				}
				this.$content = $contentNode;
			}
			return this.$content;
		},
		/*
		 * Used to a reset dashboard. Save a copy of the board spec as a string.
		 */
		setSavedInstance: function (specString) {
			this.savedInstance = specString;
		},

		getSavedInstance: function () {
			return this.savedInstance;
		},

		/**
		 * Add an object that needs to be reloaded when the dashboard view is reloaded.
		 * This is used to reload the data sources panel, properties when the dashboard is reloaded (e.g. relink)
		 * @param {string} id A name for the reloadable object
		 * @param {object} reloadable An object that has the "reload" method. The "reload" method is required on the object
		 */
		addReloadableObject: function(id, reloadable) {
			if (!this.reloadables) {
				this.reloadables = {};
			}

			// Only add the reloadable if it has the "reload" method
			if (reloadable.reload && typeof reloadable.reload === 'function') {
				this.reloadables[id] = reloadable;
			}
		},

		/*
		*	 remove and object that was previously added to the reload list
		*/
		removeReloadableObject: function(id) {
			if (this.reloadables) {
				delete this.reloadables[id];
			}
		},

		/**
		 * Important: use a collection to determine if the DatasetExecutionService needs to be setup
		 * a better glass API might be required
		 */
		_registerDatasetExecution: function() {
			return this.glassContext.appController.findCollection('com.ibm.bi.dashboard.services').then((collection) => {
				let services = collection || [];
				let setDatasetHandler = false;
				let i = 0;
				while (i < services.length && setDatasetHandler === false) {
					setDatasetHandler = 'com.ibm.bi.dashboard.services.datasetExecutionService' === services[i].id;
					i++;
				}
				if (setDatasetHandler) {
					return this.glassContext.getSvc('.DatasetExecutionService')
						.then((DatasetExecutionService) => {
							this.datasetRefreshEventHandler = DatasetExecutionService.on('loadComplete', this._onDatasetRefresh.bind(this));
						});
				}
			});
		},

		_resolveOptions: function(options) {
			// EmbedAPI param: objRef
			// set a objRef as a boardId
			options.boardId = options.boardId || options.objRef;

			// EmbedAPI param: action
			// convert action to isAuthoringMode flag
			options.isAuthoringMode = String(options.isAuthoringMode).toLowerCase() === 'true' || (options.action === 'edit');
		},

		// this method will be called by glass
		isDirty: function() {
			// isDirty is being called before the dashboardAPI is created.
			const dashboardState = this.dashboardApi ? this.dashboardApi.getFeature('DashboardState'): null;
			return dashboardState? dashboardState.getUiState().dirty : this.options.isDirtyOption;
		},

		setPermissions: function(permissions) {
			this.permissions = permissions;
		},

		isNew() {
			return !this.getBoardId();
		},

		canAuthor: function() {
			return _.indexOf(this.permissions, 'write') !== -1;
		},

		getBoardId: function() {
			return this.boardModel && this.boardModel.id;
		},

		/**
		* @returns an object containing dashboard information (isDirty, dashboard id and dashboard search path)
		*/
		getBoardInfo: function() {
			return {
				isDirty: this.isDirty(),
				boardId: this.getBoardId(),
				searchPath: this.boardModel.searchPath,
				type: 'exploration'
			};
		},

		/**
		* This method is called by glass to get the content view type which, in turn, be used to match the sharing action handlers.
		* It is overridden in <code>StoryView</code> where the 'storytelling' is returned to match its corresponding action handler.
		*/
		getType: function() {
			return 'exploration';
		},

		getLoadedWidget: function(id){
			const {
				boardLoader: {
					widgetLoader
				} = {}
			} = this;

			if (widgetLoader) {
				const widget = widgetLoader.getWidget(id);
				if (widget) {
					return widget.getAPI();
				}
			}
		},

		getContent: function(options={}) {
			const state = _.pick(this.options, 'id', 'objRef', 'options', 'subView', 'filters');

			state.isAuthoringMode = this.isAuthoringMode;

			if (this.options.boardId) {
				state.boardId = this.options.boardId;
			}

			// the glass checks the content id for the cache. This needs to be revisited after the glass cache cleanup
			if (state.objRef) {
				state.id = state.objRef;
			}
			this.id = state.id;
			if (this.boardModel && this.boardModel.id) {
				state.boardId = this.boardModel.id;
				state.id =  this.boardModel.id;
				state.objRef = this.boardModel.id;
				this.id =  this.boardModel.id;
			}

			const isBookmark = options && options.mode === this.glassContext.bookmarkMode;
			// save a version of the spec when we are not requesting the state for a bookmark
			if (!isBookmark) {
				state.boardSpec = this.options.boardSpec;

				if (this._isDashboardFeatureLoaded('DashboardState')) {
					state._isDirty = this.dashboardApi.getFeature('DashboardState').getUiState().dirty;
				} else {
					state._isDirty = options.dirty;
				}

				if (this._isDashboardFeatureLoaded('Serializer')) {
					state.boardSpec = this.dashboardApi.getFeature('Serializer').toJSON();
				}
			}

			return state;
		},

		_isDashboardFeatureLoaded: function(featureName) {
			if (this.dashboardApi) {
				return !!this.dashboardApi.getFeature(featureName);
			}

			return false;
		},

		preprocessKeyDown: e => e, // overwriteable function, implemented in child classes

		/**
		 * @param keyArr {Number | String | Array of numbers/string}
		 * @return {Boolean} returns true if and only if specified keys are pressed (ignores cmd/ctrl keys)
		 */
		isPressed(keyArr) {
			const getPressedKeyCodes = _.filter(_.keys(this._pressed),
				(key) => this._pressed[key] && [
					KeyCodes.LEFT_WINDOW_KEY, KeyCodes.CTRL,
					KeyCodes.CAPSLOCK, //filter out CapsLock because Chrome/FireFox only fire keydown event for CapsLock on
					KeyCodes.F11 //filter out F11 for FireFox, it only fire keydown event for f11
				].indexOf(parseInt(key)) == -1);
			const normalizedKeyArr = keyArr.length ? _.map(keyArr, (n) => String(n)) : [String(keyArr)];
			return _.isEqual(getPressedKeyCodes, normalizedKeyArr);
		},

		/**
		 * @return {Boolean} returns true if and only if any text is selected
		 */
		isTextSelected() {
			return window.getSelection().toString() !== '';
		},

		getECLProp(e, key) {
			let eventProps = new EventChainLocal(e);
			return eventProps.getProperty(key);
		},

		hasAuthoringCapability: function() {
			return true;
		},

		_processKeyDown: function(e) {
			e = this.preprocessKeyDown(e);

			if (e.target.tagName && e.target.tagName.match(/input|textarea|select/igm)) {
				return;
			}
			if ($(e.target).closest('[contentEditable=true]').length>0) {
				return;
			}

			// for FireFox;
			// in FireFox, CapsLock only fire keydown event, no keyup event, so key code 20 will never be remove by _processKeyUp()
			// should be removed when user turn Caps off (like Chrome)
			if (e.keyCode == KeyCodes.CAPSLOCK && this._pressed[KeyCodes.CAPSLOCK]){
				delete this._pressed[KeyCodes.CAPSLOCK];
			}else{
				this._pressed[e.keyCode] = true;
			}

			const getECLProp = () => this.getECLProp(e, 'doNotHandle');
			const canHandleKey = (arr) => {
				if (this.isPressed(arr) && !getECLProp()) {
					e.preventDefault();
					return true;
				}
				return false;
			};

			if ((e.ctrlKey || e.metaKey) && !DialogBlocker.isShowingDialogBlocker()) {
				if (canHandleKey(KeyCodes.V)) {
					if (this.isAuthorMode()) {
						// paste functionality
						this.doPaste();
					}
				} else if (canHandleKey(KeyCodes.S)) {
					if (this.hasAuthoringCapability()){
						this.doSave();
					}
				} else if (canHandleKey(KeyCodes.Y) || canHandleKey([KeyCodes.SHIFT, KeyCodes.Z])) {
					this.doRedo();
				} else if (canHandleKey(KeyCodes.Z)) {
					this.doUndo();
				} else if (canHandleKey([KeyCodes.Q, KeyCodes.FORWARDSLASH])) {
					//brings up message box, used to help debugging
					if (this.hasAuthoringCapability()){
						this._openBoardSpecificationDialog();
					}
				} else if (canHandleKey([KeyCodes.Q, KeyCodes.L])) {
					//log the Rave test spec to the console
					window.gLogRaveTestSpec = window.gLogRaveTestSpec !== true;
				} else if (!this.isTextSelected() && canHandleKey(KeyCodes.C)) {
					// copy functionality. Does not work for consumer only user when cosumer feature is on
					if (this.hasAuthoringCapability()){
						this.doCopy();
					}
				} else if (canHandleKey([KeyCodes.Q, KeyCodes.COMMA])) {
					//activate error details
					window.dashboardErrorDetailsEnabled = true;
					this.eventRouter.trigger('widget:onDetailErrors');
				}
			}

			// - The meta key in iOS blockkey up events so in the case of cmd-z/cmd-shift-z/cmd-y/cmd-s
			//the key will never actually be release causing the s, y, and z to be sticky. This is why
			//in case of the meta key they need to be release here.
			// - for multi-key combo possibilities hold off on manually triggering keyup
			if (e.metaKey && e.keyCode !== KeyCodes.Q && e.keyCode !== KeyCodes.SHIFT) {
				delete this._pressed[e.keyCode];
			}
		},

		_openBoardSpecificationDialog: function() {
			new EditableDialog({
				title: 'Board Specification',
				textContent: JSON.stringify(this.dashboardApi.getFeature('Serializer').toJSON(), null, 4),
				updateCallback: this._updateBoardModelFromDialog.bind(this)
			}).render();
		},

		/**
		 * Handler function for board model dialog to get and parse new board spec, which then updates the canvas to match the board spec
		 * @returns True if JSON is valid and false if not
		 */
		_updateBoardModelFromDialog: function(newBoardModelString) {
			// Parse JSON of board model spec the user entered. Do not handle if it is bad JSON
			try {
				this.reloadFromJSONSpec(JSON.parse(newBoardModelString));
			} catch (err) {
				this.logger.debug('Invalid JSON provided', err, this);
				this.glassContext.appController.showToast(this.stringService.get('invalidJSONResponse'), {
					'type': 'error',
					'preventDuplicates': true
				});
				return false;
			}
			return true;
		},

		doCopy: function() {
			return this.boardController.copyPasteController.doCopy();
		},

		doPaste: function() {
			return this.boardController.copyPasteController.doPaste();
		},

		_processKeyUp: function() {
			this._pressed = {};
		},

		doSave: function() {
			const savePlugin = this.glassContext.appController.findPlugin('com.ibm.bi.dashboard.saveMenu');
			if (savePlugin) {
				savePlugin.defaultButton.$el.trigger('click');
			}
		},

		doUndo: function() {
			const popover = $('.popover');
			if (popover.length > 0) {
				popover.popover('hide'); // remove popovers, they aren't updated on undo/redo and aren't cleared when clicking in the Glass buttons.
			}
			this.boardController.undoRedoController.undo();
		},

		doRedo: function() {
			const popover = $('.popover');
			if (popover.length > 0) {
				popover.popover('hide'); // remove popovers, they aren't updated on undo/redo and aren't cleared when clicking in the Glass buttons.
			}
			this.boardController.undoRedoController.redo();
		},

		_onWidgetMaximize: function () {
			this._buttonHideHelper.changeMode(this.glassContext, 'widgetMaximized');
		},

		_onWidgetRestore: function () {
			this._buttonHideHelper.changeMode(this.glassContext, this.dashboardController.getMode());
		},

		/**
		 * Toggles the event group mode.
		 * @param {String} mode The optional mode to explicitly toggle to (default: 'authoring')
		 */
		toggleEventGroupMode: function(mode) {
			const isEventGroupViewMode = this.isEventGroupViewMode;
			if (isEventGroupViewMode) {
				if (mode !== 'eventGroups') {
					// this._buttonHideHelper.changeMode(this.glassContext, mode || 'authoring');
					return this.turnOffEventGroupView(mode);
				} else {
					// we are already in eventGroup mode
					return Promise.resolve();
				}
			} else {
				this._buttonHideHelper.changeMode(this.glassContext, mode);
				return this.turnOnEventGroupView();
			}
		},

		/**
		 * Toggles between authoring & consume mode
		 */
		toggleMode: function(options) {
			// clear data sources cache
			const dataSourcesSvc = this.dashboardApi.getFeature('dataSources.deprecated');
			if (dataSourcesSvc) {
				dataSourcesSvc.clearShapingCache();
			}
			return (this.isAuthoringMode ? this.turnOffEditMode() : this.turnOnEditMode())
				.then(() => {
					this._finishModeChange(options);
				});
		},

		_addChangeModeActionToUndoRedoStack: function(options) {
			if (!options || options.sender !== 'UndoRedoController') {
				this.boardController.undoRedoController.addToUndoRedoStack(new ChangeModeAction(this, {
					glassContext: this.glassContext
				}));
			}
		},

		_triggerWindowResize: function() {
			try {
				$(window).resize();
			} catch (e) {
				console.error(e);
			}
		},

		_finishModeChange: function(options={}) {
			// We are switching to authoring mode.
			this.eventRouter.trigger('mode:change', {
				authoring: this.isAuthoringMode,
				isStoryMode: this.options.isStoryMode
			});

			// dont add if options.addToUndoRedoStack is false
			if (options.addToUndoRedoStack !== false) {
				// avoid infinite undo
				this._addChangeModeActionToUndoRedoStack(options);
			}
			// Resize to redraw the visualization because the canvas will change in size between consumption and edit mode
			// This also re-renders the grid
			this._triggerWindowResize();
		},

		isAuthorMode: function () {
			return !!this.isAuthoringMode;
		},

		turnOnEditMode: function() {
			this.isAuthoringMode = true;
			return this.lifeCycleManager.invokeLifeCycleHandlers('pre:mode.edit')
				.then(this.boardController.changeToAuthorMode.bind(this.boardController))
				.then(() => {
					// check for group view mode in authoring mode
					const isEventGroupViewMode = this.isEventGroupViewMode;
					if (isEventGroupViewMode) {
						return this.turnOnEventGroupView()
							.then(this.applyModeToGlassPlugins.bind(this));
					} else {
						this.applyModeToGlassPlugins();
					}
				})
				.then(this.lifeCycleManager.invokeLifeCycleHandlers.bind(this.lifeCycleManager, 'post:mode.edit'));
		},

		turnOffEditMode: function() {
			this.isAuthoringMode = false;
			return this.lifeCycleManager.invokeLifeCycleHandlers('pre:mode.consume')
				.then(this.boardController.changeToConsumeMode.bind(this.boardController))
				.then(this.applyModeToGlassPlugins.bind(this))
				.then(this.lifeCycleManager.invokeLifeCycleHandlers.bind(this.lifeCycleManager, 'post:mode.consume'));
		},

		turnOnEventGroupView: function() {
			this.isEventGroupViewMode = true;
			this.isAuthoringMode = true;
			this.boardController.undoRedoController.setMode('eventGroups');
			return this.boardController.changeToEventGroupMode()
				.then(() => {
					this.applyModeToGlassPlugins();
				});
		},

		turnOffEventGroupView: function(mode) {
			this.isEventGroupViewMode = false;
			this.boardController.undoRedoController.setMode(null);
			const consumptionMode = mode === 'consumption';
			return (consumptionMode ? this.turnOffEditMode() : this.turnOnEditMode())
				.then(() => this._finishModeChange({
					addToUndoRedoStack: consumptionMode
				}));
		},

		applyModeToGlassPlugins: function() {
			if (this.isAuthoringMode) {
				if (this.isEventGroupViewMode) {
					this._buttonHideHelper.changeMode(this.glassContext, 'eventGroups', this.options);
				} else {
					this._buttonHideHelper.changeMode(this.glassContext, 'authoring', this.options);
				}
			} else {
				this._buttonHideHelper.changeMode(this.glassContext, 'consume', this.options);
			}
		},

		_createServices: function(features){
			this.servicesHelper = new ServicesHelper({
				glassContext: this.glassContext,
				bootStrapFeatures: features,
				eventRouter: this.eventRouter,
				appSettings: this.getContent(),
				logger: this.logger,
				dashboardAPI: this.dashboardApi,
				internalDashboardAPI: this.internalDashboardApi
			});
			const dashboardServices = this.servicesHelper.createServices();
			this.stringService = dashboardServices.stringService;
			this.lifeCycleManager = dashboardServices.lifeCycleManager;
			this.dnDManager = dashboardServices.dnDManager;
			this.services = dashboardServices.serviceRegistry;
			this.featureLoader = dashboardServices.featureLoader;

			this.dashboardController.setServices(this.services);
			this.dashboardController.setFeatureLoader(this.featureLoader);
		},

		getDeprecatedCanvas: function(){
			if(!this.boardController) {
				//timing issue, tab layout properties tries to access this function before the
				//promise from this.boardLoader.loadBoard() gets resolved
				this.boardController = this.boardLoader  && this.boardLoader.canvasController;
			}
			if (this.boardController){
				return this.boardController.getDeprecatedCanvas();
			}
			return null;
		},

		getDeprecatedCanvasWhenReady: function(){
			if (this._boardRendered){
				return this._boardRendered.then(() => {
					return this.boardController.getDeprecatedCanvas();
				});
			}
			return Promise.reject(new Error('Unable to getCanvas . Board is not rendered yet.'));
		},

		/**
		 * NOTE: This is overridden in other components such as Explore.
		 *
		 * @returns whether the user has the dashboard capability or not.
		 */
		hasCapability: function() {
			return ErrorUtils.hasCapability(this.glassContext);
		},

		pullPreloadedBoardPromise: function() {},


		_renderImpl: function(earlyResolve, earlyReject) {

			let isEarlyPromiseResolved = false;
			const fullyLoaded = this.initialize(this.options).then(() => {
				this.$el.addClass('boardPageView');

				// Todo -- lifecycle_cleanup -- we shouldn't have to do this
				// This will create the content node .. if not done here, the toolbar will be at the bottom of the page
				// because it will be created before the content node
				this.getContentNode();

				if (!this.hasCapability()) {
					const reject = earlyReject || Promise.reject;
					return reject({
						code: 'noCapability',
						message: this.stringService.get('noDashboardCapability')
					});
				}

				this.setupKeyboardHandlers();
				this.ajaxSvc = this.glassContext.getCoreSvc('.Ajax');

				const isAuthoringMode = this.isAuthoringMode;
				this.eventRouter.on('widget:maximize', this._onWidgetMaximize, this);
				this.eventRouter.on('widget:restore', this._onWidgetRestore, this);

				PerfUtils.createPerformanceMark({
					'component': 'dashboard',
					'name': 'LoadBoard',
					'state': 'start'
				});

				this.boardModelFactory = this.boardModelFactory || new BoardModelFactory({
					dashboardAPI: this.dashboardApi,
					glassFeature: this.dashboardApi.getFeature('Glass'),
					productVersion: this.dashboardApi.getFeature('DashboardSettings').get('productVersion'),
					boardId: this.boardId,
					prefetchBoardPromise: this.pullPreloadedBoardPromise(this.boardId),
					boardSpec: this.boardSpec,
					widgetRegistry: this.widgetRegistry,
					extensions: this.extensions,
					stringResources: this.dashboardApi.getDashboardCoreSvc('.StringResources'),
					eventRouter: this.eventRouter,
					logger: this.logger,
					createLayoutModel: this.createLayoutModel
				});

				this._boardRendered = this.extensions.load()
					.then(this.widgetRegistry.initialize.bind(this.widgetRegistry, this.dashboardApi))
					.then(() => {
						PerfUtils.createPerformanceMark({
							'component': 'dashboard',
							'name': 'BoardModelFactory.createBoardModel',
							'state': 'start'
						});
						return this.boardModelFactory.createBoardModel();
					})
					.then(boardModelInfo => {
						PerfUtils.createPerformanceMark({
							'component': 'dashboard',
							'name': 'BoardModelFactory.createBoardModel',
							'state': 'end'
						});
						this.boardModel = boardModelInfo.boardModel;
						this.boardSpec = boardModelInfo.boardSpec;
						this.boardId = boardModelInfo.boardId;
					})
					.then(this.glassContext.appController.findCollection.bind(this.glassContext.appController, 'com.ibm.bi.dashboard.templates'))
					.then(templateItems => this.glassContext.appController.findCollection('com.ibm.bi.dashboard.contentTypes')
						.then(contentTypes => {
							PerfUtils.createPerformanceMark({
								'component': 'dashboard',
								'name': 'coreFeatures',
								'state': 'start'
							});
							return this.servicesHelper.createRuntimeServices({
								dashboardApi: this.getDashboardApi(),
								boardModel: this.boardModel,
								widgetRegistry: this.widgetRegistry,
								extensions: this.extensions,
								collections: {
									templates: templateItems,
									contentTypes: contentTypes
								}
							});
						})
					)
					.then(this._registerDatasetExecution.bind(this))
					.then(() => {
						PerfUtils.createPerformanceMark({
							'component': 'dashboard',
							'name': 'coreFeatures',
							'state': 'end'
						});

						if (earlyResolve){
							isEarlyPromiseResolved = true;
							earlyResolve({
								whenFullyLoaded: () => fullyLoaded
							});
						}

						this.boardLoader = new this._boardLoaderClass({

							// beging - To be removed
							glassContext: this.glassContext,
							isAuthoringMode: isAuthoringMode,
							services: this.services,
							ajaxSvc: this.ajaxSvc,
							appSettings: this.getContent(),
							// end - To be removed

							dashboardApi: this.getDashboardApi(),

							eventRouter: this.eventRouter,

							setDirty: this.boardModel.isUpgraded,

							//TOOO maybe remove
							boardSpec: this.boardSpec,
							boardModel: this.boardModel,

							el: this.getContentNode()[0],
							$viewEl: this.$el,
							widgetRegistry: this.widgetRegistry,
							gatewayUrl: 'v1',
							cdnUrl: this.getCDNUrl(),
							featureLoader: this.featureLoader,
							extensions: this.extensions,
							dashboardController: this.dashboardController
						});
						return this.boardLoader.loadBoard();
					})
					.then(() => {
						return this.boardLoader.getCanvasController()
							.then((canvasController) => {
								this.boardController = canvasController;
							});
					})
					.then(() => {

						this.canUndo = false;
						this.canRedo = false;

						this.applyModeToGlassPlugins();

						this._registerUndoRedoListener(this.buttonIds.UNDO);
						this._registerUndoRedoListener(this.buttonIds.REDO);

						this.boardModel.on('change:name', this._onNameChange, this);
						this.boardModel.on('all', this._onAllModelEvents, this);
						this.dashboardApi.getCanvas().on('all', this._manageDirtyFlagForCanvasEvents.bind(this));

						this.boardController.onPageRenderComplete().then(() => {
							PerfUtils.createPerformanceMark({
								'component': 'dashboard',
								'name': 'LoadBoard',
								'state': 'end'
							});
						});

						if (this.containerAppOptions && this.containerAppOptions.callbacks) {
							this.containerAppOptions.callbacks.resolve(this.getDashboardApi());
						}

						this.canvasExtensions = new CanvasExtensions({
							topNode: this.getCanvasExtensionsTopNode(),
							bottomNode: this.getCanvasExtensionsBottomNode(),
							splitterItems: this._splitterItems,
							canvasExtensions: this.extensions.getCanvasExtensions() || [],
							dashboardApi: this.getDashboardApi(),
							services: this.services,
							eventRouter: this.eventRouter,
							appSettings: this.getContent(),
							handlers: {
								getParentSize: () => {
									return {
										height: this.$el.height(),
										width: this.$el.width()
									};
								},
								isAuthorMode: this.isAuthorMode.bind(this),
								getSplitterState: this.getSplitterState.bind(this)
							}
						});
						this.services.register('.CanvasExtensions', this.canvasExtensions);
						this.services.register('.CopyPasteController', this.boardController.copyPasteController);

						if (this.firstRender) {
							const dashboardState = this.dashboardApi.getFeature('DashboardState');
							dashboardState.setDirty(this.options.isDirtyOption);
							this.firstRender = false;
						}
						// The dashboard is not saved yet
						if (!this.getBoardId() || this.boardLoader.setDirty) {
							const dashboardState = this.dashboardApi.getFeature('DashboardState');
							dashboardState.setDirty(true);
							if(!this.getBoardId()) {
								this.title = this.stringService.get('defaultName');
							}
							this.boardLoader.setDirty = false;
						} else {
							this.title = this.getTitle();
						}
						this.dashboardApi.getFeature('DashboardState').setActive(true);
						this.logger.debug('Authored View created', this.options, this);
						this.onCanvasReady();

						// Waiting for pageReady causes issues with the glass not showing the page at the right time.
						return this.boardController.onPageReady()
							.then( ()=> {
								PerfUtils.createPerformanceMark({
									'component': 'dashboard',
									'name': 'additionalServices',
									'state': 'start'
								});
								return this.dashboardController.onPageReady(this.isAuthoringMode);
							})
							.then(() => {
								PerfUtils.createPerformanceMark({
									'component': 'dashboard',
									'name': 'additionalServices',
									'state': 'end'
								});
								if (this.sources) {
									const dataSourcesFeature = this.dashboardApi.getFeature('DataSources');
									this.sources.forEach(source => dataSourcesFeature.addDataSource(source, false, {
										silent: true,
										payloadData: {
											skipUndoRedo: true
										}
									}));
									// Once we've added the initial source to the dataSources feature, clear the property so we don't readd them during a relink
									this.options.sources = this.sources = null;
								}
								const printFeature = this.dashboardApi.getFeature('Print');
								const dashboardPrint = this.dashboardApi.getFeature('DashboardPrint');
								printFeature.registerContent('tab', dashboardPrint);
								printFeature.registerContent('page', dashboardPrint);
							}).then(() => {
								return this.canvasExtensions.render();
							})
							.then(() => {
								return undefined;
							});
					});

				return this._boardRendered;

			}).catch ((e) => {
				if (this.logger) {
					this.logger.log('An error occured while rendering the baseBoardView');
					this.logger.error(e);
				}
				if (!isEarlyPromiseResolved && earlyReject){
					earlyReject(e);
				}

				throw e;
			});

			return fullyLoaded;
		},


		render: function() {
			BaseBoardView.inherited('render', this, arguments);
			// Only enable early rendering in consume mode for now
			// The datasource panel depends on the deprecated canvas which is available too late.
			// Once the we stop this dependency, we can resolve the render earlier for authoring mode as well.
			if (this.isEarlyRenderEnabled && !this.isAuthoringMode){
				return new Promise( (resolve, reject) => {
					this._renderImpl(resolve, reject);
				});
			}else {
				return this._renderImpl();
			}

		},
		getSplitterState: function() {
			// to be implemented by the concrete class
		},

		_onNameChange: function(event) {
			this.trigger('change:title', event);
			this.title = event.value;
		},

		_onAllModelEvents: function (event) {
			// This is done so that the edit button does not trigger the dirty flag
			const isUndoRedo = event && (event.stack === 'undo' || event.stack === 'redo');
			const isRuntimeChange = event && event.data && event.data.runtimeOnly;
			const isEventGroupsChange = !this.isAuthoringMode && event && event.eventName && event.eventName === 'change:eventGroups';
			const skipUndoRedo = event && event.data && event.data.skipUndoRedo;

			const dashboardState = this.dashboardApi.getFeature('DashboardState');
			if (!isRuntimeChange && !isEventGroupsChange) {
				const { dirty } = dashboardState.getUiState();
				if (!isUndoRedo && !dirty && !skipUndoRedo) {
					dashboardState.setDirty(true);
				} else if (isUndoRedo) {
					const isUndoRedoDirty = this.boardController.undoRedoController.isDirty();
					if (dirty && !isUndoRedoDirty) {
						dashboardState.setDirty(false);
					} else if (!dirty && isUndoRedoDirty) {
						dashboardState.setDirty(true);
					}
				}
			}
		},

		_manageDirtyFlagForCanvasEvents: function(event) {
			if (event.info && event.info.supportsUndoRedo && event.context && event.context.undoRedo !== true) {
				if (!this.isDirty()) {
					const dashboardState = this.dashboardApi.getFeature('DashboardState');
					dashboardState.setDirty(true);
				}
			}
		},

		getCanvasExtensionsTopNode: function () {
			if (!this.$top) {
				const $parentNode = this.getContentNode();
				let $top = $('.canvasExtensionTop', $parentNode);
				if ($top.length === 0) {
					$top = $('<div class="canvasExtensionTop"></div>');
					$parentNode.prepend($top);
				}
				this.$top = $top;
			}
			return this.$top;
		},
		getCanvasExtensionsBottomNode: function () {
			if (!this.$bottom) {
				let $bottom = $('.canvasExtensionBottom', this.el);
				if ($bottom.length === 0) {
					$bottom = $('<div class="canvasExtensionBottom"></div>');
					this.$el.append($bottom);
				}
				this.$bottom = $bottom;
			}
			return this.$bottom;
		},

		getDashboardApi: function() {
			return this.dashboardApi;
		},

		onCanvasReady: function() {
			// to be implemented by the concrete class
		},

		_registerUndoRedoListener: function(id) {
			const plugin = this.glassContext.appController.findPlugin(id);
			if (plugin) {
				this.boardController.undoRedoController.addListener(id, plugin);
			}
		},

		remove: function() {
			this.teardownKeyboardHandlers();
			this.eventRouter.off('widget:maximize', this._onWidgetMaximize, this);
			this.eventRouter.off('widget:restore', this._onWidgetRestore, this);

			// Destroy the view before the services  and APIs
			try {
				this.boardLoader.destroyViews();
			} catch (e) {
				if (this.logger) {
					this.logger.error(e);
				}
			}

			if (this.datasetRefreshEventHandler) {
				this.datasetRefreshEventHandler.remove();
			}

			this.boardLoader = null;

			this.dashboardController.destroy();
			this.boardModel && this.boardModel.off();

			MemUtil.destroy(this);
			BaseBoardView.inherited('remove', this, arguments);
		},

		getTitle: function() {
			return this.boardModel && this.boardModel.get('name') || '';
		},

		/**
		 * @description this will be called by glass and then glass will translate the tag 'purple' to a proper carbon color
		 * @returns {string} a color tag
		 */
		getIconColor: function() {
			return 'purple';
		},

		setTitle: function(title) {
			this.title = title;
			this.trigger('change:title', { value: title } );
		},

		openDatasetpane: function() {
			const plugin = this.glassContext.appController.findPlugin('com.ibm.bi.dashboard.dataSources.sourcesBtn');
			if (plugin && !plugin.isPressed()) {
				plugin.$el.trigger('click', {
					expandFirstEntry: true
				});
			}
		},

		setupKeyboardHandlers: function() {
			if (!this._keyDownBind) {
				this._keyDownBind = this._processKeyDown.bind(this);
				this._keyUpBind = this._processKeyUp.bind(this);
				this._windowBlur = this._processKeyUp.bind(this);

				$(document).on('keydown', this._keyDownBind);
				$(document).on('keyup', this._keyUpBind);
				$(window).on('blur', this._windowBlur);
			}
		},

		teardownKeyboardHandlers: function() {
			$(document).off('keydown', this._keyDownBind);
			$(document).off('keyup', this._keyUpBind);
			$(window).off('blur', this._windowBlur);
			this._keyDownBind = null;
			this._keyUpBind = null;
			this._windowBlur = null;
		},

		_onDatasetRefresh: function(payload) {
			this._modifiedDataset.push(payload.id);
		},

		/**
		 * Called by the glass when the view is deactivated (e.g. switching to a different view)
		 */
		deactivate: function() {
			// CADBC-892 dashboard state may not be available in hardnav scenarios
			const dashboardState = this.dashboardApi.getFeature('DashboardState');
			dashboardState && dashboardState.setActive(false);

			if (this.boardLoader) {
				this.boardLoader.deactivate();
			}
			this.teardownKeyboardHandlers();
			if(this.canvasExtensions) {
				this.canvasExtensionState = this.canvasExtensions.getState();
			}
			this.dashboardApi.triggerDashboardEvent('dashboard:deactivate');

			return BaseBoardView.inherited('deactivate', this, arguments);
		},

		/**
		 * Called by the glass just before the view is being shown
		 * @param {Object} content - content object from Glass. { perspective: xx, id: xx, content:xx }
		 */
		activate: function(content) {
			this.dashboardApi.getFeature('DashboardState').setActive(true);

			if (!this.boardLoader) {
				this.setupKeyboardHandlers();
				return BaseBoardView.inherited('activate', this, [content]);
			}

			const always = () => {
				let promise = this.boardLoader.activate();
				if (!promise || !promise.then) {
					promise = Promise.resolve();
				}

				return promise.then(() => {
					this.dashboardApi.triggerDashboardEvent('dashboard:show');
					return this.setupKeyboardHandlers();
				});
			};

			return BaseBoardView.inherited('activate', this, [content])
				.then(() => {
					// Before activating the dashboard view, check if any data source is changed, in which case we need to
					// reload metadata and re-execute widgets
					let result;
					if (this._modifiedDataset.length > 0) {
						result = this._refreshDataSource()
							.then(() => {
								this._modifiedDataset = [];
							});
					} else {
						result = Promise.resolve();
					}
					this.canvasExtensions.setState(this.canvasExtensionState);

					return result;
				}).then(always).catch(always);
		},

		/**
		 * Refresh datasources that are referenced in dashboard and were modified
		 */
		_refreshDataSource: function() {
			const dataSourcesSvc =  this.dashboardApi.getFeature('dataSources.deprecated');
			const sourcesCollection = dataSourcesSvc.getSourcesCollection();
			const aDataSources = sourcesCollection.getSources();
			const datasourcePromises = [];
			_.each(aDataSources, (source) => {
				if (this._modifiedDataset.indexOf(source.getAssetId()) >= 0) {
					datasourcePromises.push(source.reloadMetadata());
				}
			});
			return Promise.all(datasourcePromises);
		},

		_removeForReload: function() {
			this.teardownKeyboardHandlers();

			this.boardLoader.destroyViews();

			this.boardLoader = null;

			// force a re-initialize
			this.initPromise = null;

			this.boardModelFactory = null;

			if (this.datasetRefreshEventHandler) {
				this.datasetRefreshEventHandler.remove();
			}

			// This is typically destroyed in the dashbaord controller
			// but for the reload, we don't destroy the controller.
			if (this.services) {
				this.services.destroy();
			}

			// This is typically destroyed in the dashbaord controller
			// but for the reload, we don't destroy the controller.
			if (this.featureLoader) {
				this.featureLoader.destroy();
			}

			this.dashboardApi.destroy();
			this.dashboardApi = null;
			this.boardModel = null;
			this.$el.empty();
			this.$bottom = null;
			this.$top = null;
		},

		reloadFromJSONSpec: function (JSONSpec, extOptions = {}) {
			const { isAuthoringMode, clearDirtyFlag = false } = extOptions;

			this.dashboardController.toggleProperties(false);
			this._removeForReload();

			this.boardSpec = JSONSpec;
			this.isAuthoringMode = (typeof isAuthoringMode !== 'undefined') ? isAuthoringMode : this.isAuthoringMode;
			if(extOptions.boardId){
				this.boardId = extOptions.boardId; //Reload from JSON Spec with passed in board ID
			}
			return this.render().then(() => {
				if (this.reloadables) {
					Object.values(this.reloadables)
						.filter(reloadable => typeof reloadable.reload === 'function')
						.forEach(reloadable => reloadable.reload());
				}
				this.setFocus();
				// Reset the flag, otherwise the current flag state propagates
				const dashboardState = this.dashboardApi.getFeature('DashboardState');
				if (clearDirtyFlag && !this.boardLoader.setDirty) {
					dashboardState.setDirty(false);
				} else {
					dashboardState.setDirty(true);
				}
				return this.dashboardApi;
			});
		},

		/**
		 * Temp until glass provides us with an official API to know when we're visible.
		 * @return {[type]} [description]
		 */
		setFocus: function() {
			BaseBoardView.inherited('setFocus', this, arguments);
			this.lifeCycleManager.invokeLifeCycleHandlers('pre:dashboard.focus')
				.then(this.boardLoader.getCanvasController.bind(this.boardLoader))
				.then(canvasController => canvasController.layoutController.getTopLayoutViewWhenReady())
				.then(layoutView => {
					// Call onShow which will do nothing if they had already rendered or make them render correctly
					if (layoutView) {
						layoutView.onShow();
					}

					return this.lifeCycleManager.invokeLifeCycleHandlers('post:dashboard.focus');
				})
				.catch((err) => {
					this.logger.error('Failed to setFocus', err, this);
				});
		}
	});
	return BaseBoardView;
});